import { DashboardObj } from '@metrika/api';
import { atom, selector } from 'recoil';
import { useRecoilQuery } from 'recoil-toolkit';
import { assetPath, categoryPath } from '../_risk/_shared/strUtils';
import { selectorKriCatalog } from '../_risk/kri-catalog-v3/useKriCatalog';
import { selectorRiskAssessmentSidebar } from '../_risk/monitor-v3/assessment/useMonitorSidebar';
import { selectorDashboards } from '../charting/recoil';
import { selectorUserOrg } from '../user/recoil/atoms';
import { SidebarMenu } from '../user/types';
import { SearchResult } from './types';

export const lastSearchTermState = atom<string>({
   key: 'user.search.last',
   default: '',
});

export const selectorSearchIndex = selector<SearchResult[]>({
   key: 'user.search.index',
   get: ({ get }) => {
      const searchResults: SearchResult[] = [
         {
            id: 'home',
            title: 'Home > Overview',
            description: 'Overview of key risk indicators and monitoring metrics',
            link: '/risk/home/overview',
            type: 'static',
         },
      ];

      //assessments
      const { id: orgId } = get(selectorUserOrg);
      const assessments = get(selectorRiskAssessmentSidebar(orgId));
      for (const assessment of assessments) {
         searchResults.push({
            id: assessment.assessmentId,
            title: 'Monitor Risk > Assessments > ' + assessment.assetCategory + ' > ' + assessment.asset,
            description: 'Assessment of ' + assessment.asset + ' in ' + assessment.assetCategory,
            link: `/risk/monitor/${categoryPath(assessment.assetCategory)}/${assetPath(assessment.asset, assessment.protocol)}`,
            type: 'assessment',
         });
      }

      //analyze
      const dashboards = get(selectorDashboards);
      const allProtocols = Array.from(
         new Set(dashboards.map((d) => d.protocol).filter((p): p is string => !!p)),
      ).sort();
      const menuBar = allProtocols.map((protocol) => {
         const dashMenus: (SidebarMenu & { d: DashboardObj })[] = [];
         dashboards.forEach((d) => {
            if (d.protocol === protocol) {
               dashMenus.push({
                  id: `menu${d.sideMenuId || 'network'}`,
                  title: d.sideMenuLabel || (d.sideMenuId || 'network').replace(/^\w/, (c) => c.toUpperCase()),
                  menuId: d.sideMenuId,
                  urlMatch: `/${protocol}/dashboard/${d.sideMenuId}`,
                  kind: 'dashboards-menu',
                  enabled: true,
                  d: d,
               });
            }
         });
         return {
            id: protocol,
            title: protocol.replace(/^\w/, (c) => c.toUpperCase()),
            dashMenus: dashMenus.sort((a, b) => a.title.localeCompare(b.title)),
         };
      });

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const [_, element] of menuBar.entries()) {
         for (let j = 0; j < element.dashMenus.length; j++) {
            const current = element.dashMenus[j];

            searchResults.push({
               id: current.d.id,
               title:
                  'Analyze > ' + element.title + ' > ' + current.title + ' > ' + (current.d.title ?? current.d.name),
               description: current.d.description || '',
               link: `/risk/analyze/${current.d.protocol}/dashboard/${current.d.sideMenuId}/${current.d.dashModelId}`,
               type: 'dashboard',
               protocol: current.d.protocol,
            });
         }
      }

      //kri
      const kriCatalog = get(selectorKriCatalog);
      for (const kri of kriCatalog) {
         searchResults.push({
            id: kri.id,
            title: 'Discover > KRI Catalog > ' + kri.kri.asset + ' > ' + kri.title,
            description: kri.description,
            labels: kri.kri.labels ?? [],
            link: `/risk/kri-catalog/${kri.id}`,
            type: 'kri',
         });
      }

      return searchResults;
   },
});

export const useSearchIndex = () => {
   return useRecoilQuery(selectorSearchIndex);
};
